import HelpGreyOutline from "@druva-apps/orion-icons/lib/HelpGreyOutline";
import {
  MSP_ACCOUNT_STATE,
  MSP_BRAND_TYPE,
  PRODUCT_ID,
  brandTypeToName,
  dataSourceTypes,
  dateFormatter,
  productIDToName,
} from "consts";
import Button from "@druva-apps/orion/lib/button";
import Card from "@druva-apps/orion/lib/card";
import Col from "@druva-apps/orion/lib/col";
import LabelValue from "@druva-apps/orion/lib/labelValue";
import Row from "@druva-apps/orion/lib/row";
import Tooltip from "@druva-apps/orion/lib/tooltip";
import { useMSP, useMspList } from "queryHooks";
import useUpdateMSPPermission from "queryHooks/useUpdateMSPPermission/useUpdateMSPPermission";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { closeModal, openModal } from "slices/modalsSlice";
import { getStatusString } from "utils/formatter";
import "./Summary.scss";
import {
  UPDATE_PERMISSIONS_FORM_KEY,
  AllowedToDelete,
  AllowedToDeleteNow,
  AllowedToResetPassword,
  AllowedToResetPasswordAdminEmail,
  AllowedToResetPasswordExpiryDate,
  isDruInvestigateEnabled,
  isEducationLicenseEnabled,
  ExtendEvaluationPeriod,
} from ".";
import { showAlert } from "slices/alertsSlice";
import { genericErrorMessage } from "consts";
import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useMemo } from "react";
import { asLocalTime } from "utils/timezone";
import ActivationIndicator from "components/ActivationIndicator";
import MetaDataCard from "shared/meta-data-card";
import ExpandableList from "components/ExpandableList";

const Summary = () => {
  let { accountId } = useParams();
  const { data: mspData, isLoading: isMspLoading } = useMSP(accountId);
  const { mspList: allMsps, canFetchMore: isMspListLoading } = useMspList({
    includeDetails: false,
    pageSize: 1000,
  });
  const dispatch = useDispatch();

  const isEditAllowed = !(
    isMspLoading ||
    [
      MSP_ACCOUNT_STATE.creationFailed,
      MSP_ACCOUNT_STATE.markedForDeletion,
    ].includes(mspData?.status)
  );

  // Fetch parent MSP data if parentId exists
  const { data: parentMspData } = useMSP(mspData?.parentId, {
    enabled: !!mspData?.parentId,
  });

  // Find child MSPs (subordinate accounts)
  const subordinateMsps = useMemo(() => {
    if (!allMsps || !accountId) return [];
    return allMsps.filter((msp) => msp.parentId === accountId);
  }, [allMsps, accountId]);

  // Get subordinate MSP names for display
  const subordinateMspNames = useMemo(() => {
    return subordinateMsps.map((msp) => msp.organizationName);
  }, [subordinateMsps]);

  const isDellMsp = mspData?.brandType === MSP_BRAND_TYPE.dell;

  const summaryLayoutChildren = useMemo(() => {
    const children = [
      "organizationName",
      "administratorEmail",
      "expiresOn",
      "mspId",
      "sfdcId",
      "licenseType",
      "accountType",
    ];

    if (!isDellMsp) {
      children.push("subordinateMsps", "parentMsp");
    }
    children.push("druInvestigate");

    return children;
  }, [isDellMsp]);

  const { isLoading: isUpdating, mutate: updatePermission } =
    useUpdateMSPPermission(accountId, {
      onSuccess: () => {
        dispatch(closeModal(UPDATE_PERMISSIONS_FORM_KEY));
      },
      onError: (e) => {
        dispatch(
          showAlert({
            message: e?.message || genericErrorMessage,
            type: "danger",
          }),
        );
      },
    });

  const handleEditClick = useCallback(() => {
    dispatch(
      openModal({
        key: UPDATE_PERMISSIONS_FORM_KEY,
        mspId: accountId,
        initialVaue: {
          [AllowedToDelete]: mspData?.permissions?.[AllowedToDelete],
          [AllowedToDeleteNow]: mspData?.permissions?.[AllowedToDeleteNow],
          [AllowedToResetPassword]:
            mspData?.permissions?.[AllowedToResetPassword],
          [AllowedToResetPasswordAdminEmail]:
            mspData?.permissions?.[AllowedToResetPasswordAdminEmail],
          [ExtendEvaluationPeriod]:
            mspData?.permissions?.[ExtendEvaluationPeriod],
        },
        updatePermission: updatePermission,
      }),
    );
  }, [updatePermission, dispatch, accountId, mspData?.permissions]);

  return (
    <>
      <MetaDataCard
        cardProps={{
          title: "Summary",
        }}
        isLoading={isMspLoading}
        data={{
          organizationName: mspData?.organizationName,
          administratorEmail: mspData?.email,
          expiresOn: mspData?.expiryDate
            ? dateFormatter.format(asLocalTime(mspData?.expiryDate))
            : "-",
          mspId: mspData?.id,
          sfdcId: mspData?.sfdcId,
          licenseType: mspData?.edition,
          accountType:
            brandTypeToName[mspData?.brandType ?? MSP_BRAND_TYPE.druva],
          parentMsp: parentMspData?.organizationName || "-",
          subordinateMsps: subordinateMspNames,
        }}
        metadata={{
          fields: {
            organizationName: {
              // label: "Organization Name",
              // TODO: This should be Organization name and not customer name, will handle this in separate ticket and not in feature ticket
              label: "Customer Name",
              truncateValue: true,
            },
            administratorEmail: {
              label: "Administrator Email",
              truncateValue: true,
            },
            expiresOn: {
              label: "Expires On",
            },
            mspId: {
              label: "MSP ID",
              truncateValue: true,
            },
            sfdcId: {
              label: "SFDC ID",
              truncateValue: true,
            },
            licenseType: {
              label: "License Type",
              truncateValue: true,
              className: "oui-text-capitalize",
            },
            accountType: {
              label: "Account Type",
              truncateValue: true,
            },
            parentMsp: {
              label: "Parent MSP Account",
              truncateValue: true,
            },
            subordinateMsps: {
              label: "Subordinate MSP Accounts",
              isLoading: isMspListLoading,
              renderValue: (props) => {
                if (subordinateMspNames.length > 0) {
                  return (
                    <ExpandableList
                      items={props.value}
                      maxVisibleItems={2}
                      dataTestId="subordinate-msp-list"
                    />
                  );
                }
                return "-";
              },
            },
            druInvestigate: {
              label: "Dru Investigate",
              renderValue: () => {
                return (
                  <ActivationIndicator
                    isActive={isDruInvestigateEnabled(mspData?.features)}
                  />
                );
              },
            },
          },
          layout: {
            groups: [
              {
                key: "summary",
                title: "",
                children: summaryLayoutChildren,
              },
            ],
          },
        }}
      />
      <Row className="oui-mt-4 oui-mb-4">
        {mspData?.products?.map((details, idx, arr) => {
          const isLastItem = idx === arr.length - 1;
          const {
            productID,
            skus,
            awsStorageRegions,
            azureStorageRegions,
            datasources,
            features,
          } = details;

          const isProductSaasAppsAndEndpoints =
            productID === PRODUCT_ID.saasAppsAndEndpoints;
          const isProductEnterpriseWorkloads =
            productID === PRODUCT_ID.enterpriseWorkloads;
          return (
            <Col
              sm={12}
              key={`license-card-${productID}`}
              className={isLastItem ? "" : "oui-mb-4"}
            >
              <Card
                title={productIDToName[productID]}
                fullHeight
                body={() => (
                  <>
                    <Row className="oui-mb-4">
                      <Col sm={3}>
                        <LabelValue
                          label="Editions"
                          className="oui-text-capitalize"
                          value={skus.join(", ")}
                          truncateValue
                        />
                      </Col>
                      <Col sm={3}>
                        <LabelValue
                          label="AWS Storage Regions"
                          value={awsStorageRegions.join(", ")}
                          truncateValue
                        />
                      </Col>
                      <Col sm={3}>
                        <LabelValue
                          label="Azure Storage Regions"
                          value={(azureStorageRegions || []).join(", ") || "-"}
                          truncateValue
                        />
                      </Col>
                      {isProductSaasAppsAndEndpoints && (
                        <Col sm={3}>
                          <LabelValue
                            label="Products"
                            className="oui-text-capitalize"
                            value={datasources
                              ?.filter((ds) => ds.isEnabled)
                              ?.map((ds) => dataSourceTypes[ds.name] || ds.name)
                              ?.join(", ")}
                            truncateValue
                          />
                        </Col>
                      )}
                    </Row>

                    {isProductSaasAppsAndEndpoints && (
                      <Row>
                        <Col sm={3}>
                          <ActivationIndicator
                            label="Education License"
                            isActive={isEducationLicenseEnabled(features)}
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              />
            </Col>
          );
        })}
      </Row>

      <MetaDataCard
        isLoading={isUpdating || isMspLoading}
        cardProps={{
          title: () => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Card.Title collapsible title="Advance Settings" />
              <Tooltip
                text="With this option enabled , you are letting MSP delete their customers"
                placement="right"
              >
                <HelpGreyOutline
                  width={12}
                  height={12}
                  className="oui-ml-2"
                  muted={false}
                />
              </Tooltip>
            </div>
          ),
          titleAction: () =>
            isEditAllowed && (
              <Button
                onClick={handleEditClick}
                label="Edit"
                dataTestId="advance-setting-edit-button"
              />
            ),
        }}
        data={{
          enableDeleteTenant: getStatusString(
            mspData?.permissions?.[AllowedToDelete],
          ),
          allowDeleteImmediately: getStatusString(
            mspData?.permissions?.[AllowedToDeleteNow],
            true,
          ),
          enableResetPassword: getStatusString(
            mspData?.permissions?.[AllowedToResetPassword],
          ),
          emailConfiguredForPasswordReset:
            mspData?.permissions?.[AllowedToResetPasswordAdminEmail] || "-",
          passwordResetAccessExpiry: mspData?.permissions?.[
            AllowedToResetPasswordExpiryDate
          ]
            ? `${formatInTimeZone(
                mspData?.permissions?.[AllowedToResetPasswordExpiryDate],
                "UTC",
                "MMM dd YYY HH:mm:ss",
              )} UTC`
            : "-",
          extendEvaluationPeriod:
            mspData?.permissions?.[ExtendEvaluationPeriod],
        }}
        metadata={{
          layout: {
            groups: [
              {
                key: "advanceSettings",
                title: "",
                children: [
                  "enableDeleteTenant",
                  "allowDeleteImmediately",
                  "",
                  "",
                  "enableResetPassword",
                  "emailConfiguredForPasswordReset",
                  "passwordResetAccessExpiry",
                  "extendEvaluationPeriod",
                ],
              },
            ],
          },
          fields: {
            enableDeleteTenant: {
              label: "Enable Delete Tenant",
            },
            allowDeleteImmediately: {
              label: "Allow Delete Immediately",
            },
            enableResetPassword: {
              label: "Enable Reset Password",
            },
            emailConfiguredForPasswordReset: {
              label: "Email Configured for Password Reset",
            },
            passwordResetAccessExpiry: {
              label: "Password Reset Access Expiry",
            },
            extendEvaluationPeriod: {
              label: "Evaluation License Period",
            },
          },
        }}
      />
    </>
  );
};
export default Summary;
